/**
 * Locale store — Zustand, persisted, with side-effects on the HTML
 * document (lang + dir) so Tailwind RTL logical properties resolve
 * correctly without a re-render.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import i18n, { isRtl, type Locale } from '@/i18n/config';

interface LocaleState {
  locale: Locale;
  setLocale: (l: Locale) => void;
}

function applyToDom(locale: Locale) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('lang', locale);
  root.setAttribute('dir', isRtl(locale) ? 'rtl' : 'ltr');
}

function applyToI18n(locale: Locale) {
  if (i18n.language !== locale) {
    void i18n.changeLanguage(locale);
  }
}

export const useLocaleStore = create<LocaleState>()(
  persist(
    (set) => ({
      locale: resolveInitialLocale(),
      // English-only product: every screen renders in English. `setLocale` is
      // pinned to 'en' so neither the (removed) toggle nor a stale persisted
      // value can ever switch the UI to Hebrew.
      setLocale: () => {
        applyToDom('en');
        applyToI18n('en');
        set({ locale: 'en' });
      },
    }),
    {
      name: 'gi-engine.locale',
      storage: createJSONStorage(() => localStorage),
      // Force English on rehydrate too — overrides any previously-persisted
      // Hebrew choice from before the product became English-only.
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.locale = 'en';
        }
        applyToDom('en');
        applyToI18n('en');
      },
      partialize: (state) => ({ locale: state.locale }),
    },
  ),
);

function resolveInitialLocale(): Locale {
  // English-only product. Always 'en'.
  return 'en';
}
